import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useOpsExecution } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Circle, FileCheck2, Loader2, LockKeyhole, Play, Truck } from "lucide-react";

const label = (value) => value?.split("_").map((x) => x[0] + x.slice(1).toLowerCase()).join(" ");

export default function ExecutionPanel({ job, documents = [] }) {
  const qc = useQueryClient();
  const enabled = ["READY_FOR_EXECUTION", "IN_PROGRESS", "DEPARTED", "ARRIVED", "CLEARANCE", "DELIVERY", "DELIVERED"].includes(job.status);
  const { data, isLoading } = useOpsExecution(job.id, enabled);
  const [busy, setBusy] = useState(false);
  const [evidence, setEvidence] = useState("");
  const [documentId, setDocumentId] = useState("");
  const next = data?.nextMilestone;

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["ops-execution", job.id] }),
      qc.invalidateQueries({ queryKey: ["job", job.id] }),
      qc.invalidateQueries({ queryKey: ["actions", "finance"] }),
    ]);
  };
  const start = async () => {
    setBusy(true);
    try { await api.post(`/ops/jobs/${job.id}/execution/start`, { source: "OPERATIONS_UI" }); await refresh(); toast.success("Controlled shipment execution started"); }
    catch (e) { toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Execution is blocked"); }
    finally { setBusy(false); }
  };
  const complete = async () => {
    if (!evidence.trim() && !documentId) return toast.error("Add an evidence reference or linked document");
    if (next?.type === "POD" && !documentId) return toast.error("Select the uploaded POD document");
    setBusy(true);
    try {
      await api.post(`/ops/jobs/${job.id}/execution/milestones/${next.type}/complete`, {
        actualAt: new Date().toISOString(), source: "MANUAL_CONFIRMATION",
        evidenceReference: evidence.trim() || undefined, documentId: documentId || undefined,
      });
      setEvidence(""); setDocumentId(""); await refresh();
      toast.success(next.type === "POD" ? "POD verified — Finance invoice review created" : `${label(next.type)} recorded`);
    } catch (e) { toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Milestone could not be recorded"); }
    finally { setBusy(false); }
  };

  if (!enabled) return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 flex gap-3">
      <LockKeyhole className="h-5 w-5 text-slate-400 shrink-0" />
      <div><div className="font-semibold text-slate-800">Execution is not available yet</div><div className="mt-0.5">Complete Operations handoff, booking confirmation, cutoffs and readiness checks first.</div></div>
    </div>
  );
  if (isLoading) return <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">Loading execution controls…</div>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="execution-panel">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div><div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Truck className="h-4 w-4 text-indigo-600" />Shipment execution</div><div className="text-xs text-slate-500 mt-0.5">Actual events require evidence and follow the approved sequence.</div></div>
        {data?.podVerified && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5" />POD verified</span>}
      </div>
      <div className="p-5">
        <div className="space-y-0">
          {(data?.milestones || []).map((m, i) => {
            const done = m.status === "COMPLETED";
            return <div key={m.type} className="flex gap-3 min-h-[54px]">
              <div className="flex flex-col items-center"><span className={`h-6 w-6 rounded-full flex items-center justify-center ${done ? "bg-emerald-100 text-emerald-700" : m.type === next?.type ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"}`}>{done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}</span>{i < data.milestones.length - 1 && <span className={`w-px flex-1 ${done ? "bg-emerald-200" : "bg-slate-200"}`} />}</div>
              <div className="pb-4 flex-1"><div className="text-sm font-medium text-slate-800">{label(m.type)}</div><div className="text-[11px] text-slate-400">{done ? `${new Date(m.actualAt).toLocaleString()} · ${m.evidenceReference || m.documentId}` : m.type === next?.type ? "Next required event" : "Pending"}</div></div>
            </div>;
          })}
        </div>
        {job.status === "READY_FOR_EXECUTION" ? (
          <Button onClick={start} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="execution-start">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}Start execution</Button>
        ) : next ? (
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3.5">
            <div className="text-xs font-semibold text-indigo-900 mb-2">Record {label(next.type)}</div>
            <div className="grid gap-2 md:grid-cols-[1fr_220px_auto]">
              <Input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Evidence / carrier reference" data-testid="execution-evidence" />
              <Select value={documentId} onValueChange={setDocumentId}><SelectTrigger data-testid="execution-document"><SelectValue placeholder={next.type === "POD" ? "Select POD document*" : "Link document (optional)"} /></SelectTrigger><SelectContent>{documents.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.id}</SelectItem>)}</SelectContent></Select>
              <Button onClick={complete} disabled={busy} data-testid="execution-complete">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm event</Button>
            </div>
            {next.type === "POD" && <div className="text-[11px] text-amber-700 mt-2">An uploaded POD document is mandatory. Verifying it marks the Job Delivered and creates Finance work.</div>}
          </div>
        ) : <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">Delivery evidence is complete. This Job is ready for invoice preparation.</div>}
      </div>
    </div>
  );
}
